import { useCallback, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import LoadingButton from "@mui/lab/LoadingButton";
import { Box, Button, Stack } from "@mui/material";
import { Form, Formik, FormikProps } from "formik";

import { PermissionsField } from "common/components/PermissionsField/PermissionsField";
import { permissions } from "constants/constants";
import { useSelectedOrganization } from "hooks/useSelectedOrganization";
import { useUpdateSelectedOrganizationApiToken } from "hooks/useUpdateSelectedOrganizationApiToken";
import { OrganizationApiToken } from "types/Organization";
import { getPermissionsByOrgId } from "utils/localStorage";

interface PermissionsTabProps {
  token: OrganizationApiToken;
}

interface FormValues {
  permissions: Array<string>;
}

export const PermissionsTab = ({ token }: PermissionsTabProps): JSX.Element => {
  const { selectedOrganizationId } = useSelectedOrganization();
  const organizationPermissions = getPermissionsByOrgId(selectedOrganizationId);
  const isEditable = useMemo(
    () => (organizationPermissions ?? []).includes(permissions.APITOKEN_UPDATE),
    [organizationPermissions]
  );
  return (
    <>
      {isEditable ? (
        <EditablePermissions token={token} />
      ) : (
        <NonEditablePermissions token={token} />
      )}
    </>
  );
};

const EditablePermissions = ({ token }: PermissionsTabProps): JSX.Element => {
  const { t } = useTranslation();
  const { isLoading, onUpdateApiToken } = useUpdateSelectedOrganizationApiToken(
    {
      tokenId: token.id,
    }
  );
  const initialValues = useMemo(
    () => ({
      permissions: token.permissions?.map((permission) => permission) ?? [],
    }),
    [token.permissions]
  );
  const formRef = useRef<FormikProps<FormValues>>(null);

  const onReset = useCallback(() => formRef.current?.handleReset(), [formRef]);
  const onSave = useCallback(() => formRef.current?.handleSubmit(), [formRef]);

  const onSubmit = (values: FormValues) => {
    return onUpdateApiToken({
      id: token.id,
      name: token.name,
      description: token.description,
      settings: token.settings,
      permissions: values.permissions.map((id) => id),
    });
  };

  return (
    <Stack sx={{ height: "100%" }}>
      <Formik
        initialValues={initialValues}
        onSubmit={onSubmit}
        innerRef={formRef}
      >
        <Form
          style={{
            // HACK: 62 magic number (42 height)
            height: "calc(100% - 42px)",
            marginBottom: 2,
            overflow: "auto",
          }}
        >
          <Box paddingY={1}>
            <PermissionsField
              applicationName={token.applicationName ?? ""}
              name="permissions"
              columns={2}
            />
          </Box>
        </Form>
      </Formik>
      <Stack direction="row" justifyContent="flex-end" pt={2}>
        <Button sx={{ mr: 2 }} onClick={onReset}>
          {t("Reset")}
        </Button>
        <LoadingButton onClick={onSave} loading={isLoading} variant="contained">
          {t("UpdatePermissions")}
        </LoadingButton>
      </Stack>
    </Stack>
  );
};

const NonEditablePermissions = ({
  token,
}: PermissionsTabProps): JSX.Element => {
  // const groupedPermissions = useMemo(
  //   () => groupPermissionsByType(token.permissions ?? []),
  //   [token]
  // );
  // return (
  //   <NonEditablePermissionList
  //     columns={3}
  //     groupedPermissions={groupedPermissions}
  //   />
  // );

  return <div>Commented code</div>;
};
